/**
 * Shopping Carts Tab - Redesigned
 * Modern list/card view with filters, sorting, and pagination
 * Matches Customers page design
 */

import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../../services/api';
import {
  ShoppingCart,
  RefreshCw,
  Search,
  AlertCircle,
  Package,
  Calendar,
  Clock,
  Mail,
  User,
  DollarSign,
  Grid3x3,
  List,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  Filter,
} from 'lucide-react';

interface CartItem {
  productId: number;
  productName: string;
  variantName?: string;
  quantity: number;
  price: number;
  subtotal: number;
}

interface ShoppingCartData {
  _id: string;
  cartId: number;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  items: CartItem[];
  totalItems: number;
  totalValue: number;
  status: 'active' | 'abandoned' | 'converted' | 'recovered';
  emailsSent?: number;
  lastEmailSentAt?: string;
  emailStatus?: 'pending' | 'sent' | 'failed' | 'opened' | 'clicked' | 'bounced';
  convertedAt?: string;
  abandonedAt?: string;
  recoveredFromEmail?: boolean;
  orderId?: string;
}

interface EshopShoppingCartsTabProps {
  eshopId: string;
  eshopConfig?: any;
  companyCurrency?: string;
}

type ViewMode = 'list' | 'card';
type SortColumn = 'createdAt' | 'lastActivityAt' | 'totalValue' | 'totalItems' | 'status';
type SortDirection = 'asc' | 'desc';

const EshopShoppingCartsTab: React.FC<EshopShoppingCartsTabProps> = ({
  eshopId,
  eshopConfig,
  companyCurrency = 'GBP',
}) => {
  const { accessToken } = useAuthStore();
  const { getSelection } = useDataSourceStore();
  const selection = getSelection();
  const companyId = selection?.companyId;

  // State
  const [carts, setCarts] = useState<ShoppingCartData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCarts, setTotalCarts] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [sortBy, setSortBy] = useState<SortColumn>('lastActivityAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedCart, setExpandedCart] = useState<string | null>(null);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [minValue, setMinValue] = useState<string>('');
  const [maxValue, setMaxValue] = useState<string>('');
  const [dateRange, setDateRange] = useState<string>('all');
  const [itemCountFilter, setItemCountFilter] = useState<string>('all');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  const productDropdownRef = useRef<HTMLDivElement>(null);

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    
    if (showProductDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProductDropdown]);

  const cartsPerPage = 20;

  // Fetch ALL unique products from backend
  const [allProducts, setAllProducts] = React.useState<string[]>([]);
  const [loadingProducts, setLoadingProducts] = React.useState(false);

  React.useEffect(() => {
    const fetchAllProducts = async () => {
      if (!companyId || !eshopId) return;
      
      setLoadingProducts(true);
      try {
        const response = await api.get(
          `/api/v1/eshops/${eshopId}/unique-products?companyId=${companyId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        
        if (response.data.success) {
          const products = response.data.data || [];
          setAllProducts(products);
          console.log(`📦 Loaded ${products.length} unique products from ALL customers`);
        }
      } catch (error) {
        console.error('Failed to fetch unique products:', error);
        setAllProducts([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchAllProducts();
  }, [companyId, eshopId, accessToken]);

  // Filter products by search term for autocomplete
  const filteredProducts = React.useMemo(() => {
    if (!productSearchTerm) return allProducts;
    return allProducts.filter(p => 
      p.toLowerCase().includes(productSearchTerm.toLowerCase())
    );
  }, [allProducts, productSearchTerm]);

  useEffect(() => {
    fetchCarts();
  }, [eshopId, companyId, searchTerm]); // Removed currentPage - we'll paginate client-side

  const fetchCarts = async () => {
    if (!companyId || !eshopId) {
      setError('Missing company or e-shop ID');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1000', // Fetch all carts (increased limit)
        search: searchTerm,
        companyId: companyId,
      });

      const response = await api.get(
        `/api/v1/eshops/${eshopId}/shopping-carts?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.data.success) {
        setCarts(response.data.data || []);
        setTotalCarts(response.data.pagination?.total || 0);
      } else {
        throw new Error(response.data.message || 'Failed to fetch carts');
      }
    } catch (err: any) {
      console.error('Failed to fetch carts:', err);
      setError(err.message || 'Failed to load shopping carts');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    const symbol = companyCurrency === 'GBP' ? '£' : '$';
    return `${symbol}${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const minutes = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  // Client-side filtering and sorting
  const filteredAndSortedCarts = React.useMemo(() => {
    let filtered = [...carts];

    // Filter by status
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Filter by date range
    if (dateRange && dateRange !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (dateRange) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case 'quarter':
          cutoffDate.setDate(now.getDate() - 90);
          break;
      }
      
      filtered = filtered.filter(c => new Date(c.createdAt) >= cutoffDate);
    }

    // Filter by value range
    if (minValue) {
      filtered = filtered.filter(c => c.totalValue >= parseFloat(minValue));
    }
    if (maxValue) {
      filtered = filtered.filter(c => c.totalValue <= parseFloat(maxValue));
    }

    // Filter by item count
    if (itemCountFilter && itemCountFilter !== 'all') {
      filtered = filtered.filter(c => {
        const itemCount = c.totalItems;
        switch (itemCountFilter) {
          case '1':
            return itemCount === 1;
          case '2-5':
            return itemCount >= 2 && itemCount <= 5;
          case '6-10':
            return itemCount >= 6 && itemCount <= 10;
          case '11+':
            return itemCount >= 11;
          default:
            return true;
        }
      });
    }

    // Filter by selected products
    if (selectedProducts.length > 0) {
      filtered = filtered.filter(cart => {
        return cart.items?.some(item => {
          const fullName = item.variantName 
            ? `${item.productName} (${item.variantName})`
            : item.productName;
          return selectedProducts.includes(fullName);
        });
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any = a[sortBy];
      let bVal: any = b[sortBy];

      if (sortBy === 'createdAt' || sortBy === 'lastActivityAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [carts, statusFilter, dateRange, minValue, maxValue, itemCountFilter, selectedProducts, sortBy, sortDirection]);

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4 opacity-0 group-hover:opacity-50" />;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  // Paginate the filtered results
  const paginatedCarts = React.useMemo(() => {
    const startIndex = (currentPage - 1) * cartsPerPage;
    const endIndex = startIndex + cartsPerPage;
    return filteredAndSortedCarts.slice(startIndex, endIndex);
  }, [filteredAndSortedCarts, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedCarts.length / cartsPerPage);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, dateRange, minValue, maxValue, itemCountFilter, selectedProducts]);

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchCarts}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search & Filters Bar */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search by customer name or email..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2.5 rounded-lg border transition-colors flex items-center gap-2 ${
            showFilters
              ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-600 dark:text-indigo-400'
              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Filter className="w-5 h-5" />
          Filters
        </button>
        <button
          onClick={fetchCarts}
          disabled={loading}
          className="p-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-gray-600"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
            title="List view"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'card'
                ? 'bg-white dark:bg-gray-600 text-indigo-600 dark:text-indigo-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
            title="Card view"
          >
            <Grid3x3 className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters Panel - MATCHES CUSTOMERS TAB */}
      {showFilters && (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="active">🛒 Active</option>
              <option value="abandoned">⚠️ Abandoned</option>
              <option value="converted">✅ Converted</option>
              <option value="recovered">🔄 Recovered</option>
            </select>

            {/* Date Range Filter */}
            <select
              value={dateRange}
              onChange={e => {
                setDateRange(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Time</option>
              <option value="today">📅 Today</option>
              <option value="week">📆 Last 7 days</option>
              <option value="month">🗓️ Last 30 days</option>
              <option value="quarter">🗓️ Last 90 days</option>
            </select>

            {/* Item Count Filter */}
            <select
              value={itemCountFilter}
              onChange={e => {
                setItemCountFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto min-w-[140px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm"
            >
              <option value="all">All Items</option>
              <option value="1">1 item</option>
              <option value="2-5">2-5 items</option>
              <option value="6-10">6-10 items</option>
              <option value="11+">11+ items</option>
            </select>

            {/* Min Value */}
            <input
              type="number"
              placeholder="Min £"
              value={minValue}
              onChange={e => {
                setMinValue(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto min-w-[100px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400"
            />

            {/* Max Value */}
            <input
              type="number"
              placeholder="Max £"
              value={maxValue}
              onChange={e => {
                setMaxValue(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto min-w-[100px] px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400"
            />

            {/* Product Multi-Select Filter */}
            <div ref={productDropdownRef} className="relative w-full sm:w-auto min-w-[200px]">
              <button
                onClick={() => setShowProductDropdown(!showProductDropdown)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm text-left flex items-center justify-between"
              >
                <span className={selectedProducts.length === 0 ? 'text-gray-500 dark:text-gray-400' : ''}>
                  {selectedProducts.length === 0 
                    ? '📦 Filter by Product' 
                    : `${selectedProducts.length} product${selectedProducts.length > 1 ? 's' : ''} selected`
                  }
                </span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
              
              {showProductDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full min-w-[300px] max-w-[400px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-[300px] overflow-hidden">
                  {/* Search Input */}
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchTerm}
                      onChange={e => setProductSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white text-sm placeholder-gray-500 dark:placeholder-gray-400"
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                  
                  {/* Product List */}
                  <div className="overflow-y-auto max-h-[220px]">
                    {loadingProducts ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2" />
                        Loading products...
                      </div>
                    ) : filteredProducts.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        {allProducts.length === 0 ? 'No products found' : 'No matching products'}
                      </div>
                    ) : (
                      filteredProducts.map(product => (
                        <label
                          key={product}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product)}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedProducts([...selectedProducts, product]);
                              } else {
                                setSelectedProducts(selectedProducts.filter(p => p !== product));
                              }
                              setCurrentPage(1);
                            }}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-gray-900 dark:text-gray-100 truncate">{product}</span>
                        </label>
                      ))
                    )}
                  </div>
                  
                  {/* Footer Actions */}
                  {selectedProducts.length > 0 && (
                    <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {selectedProducts.length} selected
                      </span>
                      <button
                        onClick={() => {
                          setSelectedProducts([]);
                          setCurrentPage(1);
                        }}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {(statusFilter !== 'all' || dateRange !== 'all' || itemCountFilter !== 'all' || minValue || maxValue || selectedProducts.length > 0) && (
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setDateRange('all');
                  setItemCountFilter('all');
                  setMinValue('');
                  setMaxValue('');
                  setSelectedProducts([]);
                  setCurrentPage(1);
                }}
                className="ml-auto px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm flex items-center gap-1 transition-colors"
                title="Clear all filters"
              >
                <RefreshCw className="w-3 h-3" />
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Shopping Carts</h3>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredAndSortedCarts.length < carts.length ? (
              <>
                Showing:{' '}
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {filteredAndSortedCarts.length.toLocaleString()}
                </span>
                {' of '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {carts.length.toLocaleString()}
                </span>
                {' carts'}
              </>
            ) : (
              <>
                Total:{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {carts.length.toLocaleString()}
                </span>
                {' carts'}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredAndSortedCarts.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' || dateRange !== 'all' || minValue || maxValue
              ? 'No carts found matching your filters'
              : 'No shopping carts yet'}
          </p>
        </div>
      ) : (
        <>
          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                        Customer
                      </th>
                      <th
                        onClick={() => handleSort('totalItems')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          Items
                          <SortIcon column="totalItems" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('totalValue')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          Value
                          <SortIcon column="totalValue" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          Status
                          <SortIcon column="status" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('createdAt')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          Created
                          <SortIcon column="createdAt" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('lastActivityAt')}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors group"
                      >
                        <div className="flex items-center gap-2">
                          Last Activity
                          <SortIcon column="lastActivityAt" />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                        Products
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedCarts.map(cart => (
                      <React.Fragment key={cart._id}>
                        <tr
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => setExpandedCart(expandedCart === cart._id ? null : cart._id)}
                        >
                          {/* Customer */}
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {cart.firstName} {cart.lastName}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {cart.email}
                              </div>
                            </div>
                          </td>

                          {/* Items */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                              <Package className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{cart.totalItems}</span>
                            </div>
                          </td>

                          {/* Value */}
                          <td className="px-4 py-3">
                            <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                              {formatCurrency(cart.totalValue)}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-3">
                            <span
                              className={`px-3 py-1 text-xs font-medium rounded-full ${
                                cart.status === 'active'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : cart.status === 'abandoned'
                                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                                  : cart.status === 'converted'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              }`}
                            >
                              {cart.status.charAt(0).toUpperCase() + cart.status.slice(1)}
                            </span>
                          </td>

                          {/* Created */}
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {formatDate(cart.createdAt)}
                            </div>
                          </td>

                          {/* Last Activity */}
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {formatRelativeTime(cart.lastActivityAt)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {formatDate(cart.lastActivityAt)}
                            </div>
                          </td>

                          {/* Products Preview */}
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedCart(expandedCart === cart._id ? null : cart._id);
                              }}
                              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 flex items-center gap-1"
                            >
                              {expandedCart === cart._id ? (
                                <>
                                  <ChevronUp className="w-4 h-4" />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4" />
                                  View ({cart.items.length})
                                </>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* Expanded Product Details */}
                        {expandedCart === cart._id && (
                          <tr>
                            <td colSpan={7} className="px-4 py-4 bg-gray-50 dark:bg-gray-900">
                              <div className="space-y-2">
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                                  Cart Items:
                                </h4>
                                <div className="space-y-2">
                                  {cart.items.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                                    >
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900 dark:text-white">
                                          {item.productName}
                                        </div>
                                        {item.variantName && (
                                          <div className="text-sm text-gray-600 dark:text-gray-400">
                                            Variant: {item.variantName}
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 text-sm">
                                        <div className="text-gray-600 dark:text-gray-400">
                                          Qty: <span className="font-medium">{item.quantity}</span>
                                        </div>
                                        <div className="text-gray-600 dark:text-gray-400">
                                          Price: <span className="font-medium">{formatCurrency(item.price)}</span>
                                        </div>
                                        <div className="font-semibold text-gray-900 dark:text-white min-w-[80px] text-right">
                                          {formatCurrency(item.subtotal)}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex justify-end pt-3 border-t border-gray-200 dark:border-gray-700">
                                  <div className="text-right">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                      Total Value:
                                    </div>
                                    <div className="text-xl font-bold text-green-600 dark:text-green-400">
                                      {formatCurrency(cart.totalValue)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Card View */}
          {viewMode === 'card' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredAndSortedCarts.map(cart => (
                <div
                  key={cart._id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-shadow"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {cart.firstName} {cart.lastName}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {cart.email}
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        cart.status === 'active'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : cart.status === 'abandoned'
                          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                          : cart.status === 'converted'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}
                    >
                      {cart.status.charAt(0).toUpperCase() + cart.status.slice(1)}
                    </span>
                  </div>

                  {/* Cart Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Items</div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-gray-400" />
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {cart.totalItems}
                        </span>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Value</div>
                      <div className="font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(cart.totalValue)}
                      </div>
                    </div>
                  </div>

                  {/* Products List */}
                  <div className="space-y-2 mb-4">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase">
                      Products:
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {cart.items.map((item, idx) => (
                        <div
                          key={idx}
                          className="text-sm bg-gray-50 dark:bg-gray-700 rounded p-2"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white text-xs">
                                {item.productName}
                              </div>
                              {item.variantName && (
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  {item.variantName}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400 ml-2">
                              ×{item.quantity}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Timestamps */}
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <Calendar className="w-3 h-3" />
                      Created: {formatDate(cart.createdAt)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <Clock className="w-3 h-3" />
                      Last activity: {formatRelativeTime(cart.lastActivityAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {totalPages} • Showing {paginatedCarts.length} of{' '}
                {filteredAndSortedCarts.length.toLocaleString()} carts
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EshopShoppingCartsTab;
